import {
  CountryPicker,
  KdsButtonIcon,
  KdsCallout,
  KdsText,
  KdsTextField,
  Phone,
} from "@kazaar-platform/design-system";
import { RiArrowLeftSLine } from "@remixicon/react";
import { useTranslate } from "@tolgee/react";
import { trim } from "lodash";
import { Controller, useFormContext } from "react-hook-form";

import { type AddressFormSchema } from "./AddressForm.schema";
import { type AddressFormProps } from "./AddressForm.types";

export const AddressForm = ({ addressToEdit, onCancel }: AddressFormProps) => {
  // hooks
  const { t } = useTranslate();

  // Form context
  const methods = useFormContext<AddressFormSchema>();

  return (
    <>
      <div className="sticky left-0 top-0 z-30 flex flex-col gap-sp-xs bg-white pb-sp-md">
        <div className="flex items-center gap-sp-xs">
          <KdsButtonIcon
            icon={RiArrowLeftSLine}
            variant="ghost"
            color="neutral"
            size="small"
            onPress={onCancel}
          />
          <KdsText size="title-md" as="h2">
            {addressToEdit?.id
              ? t("addressDrawer.addressForm.titleEdit")
              : t("addressDrawer.addressForm.titleAdd")}
          </KdsText>
        </div>

        <KdsCallout
          visible
          state="info"
          title={t("addressDrawer.addressForm.callout")}
        />
      </div>
      <div className="flex flex-col justify-between">
        <div className="flex flex-col gap-sp-md py-sp-xs">
          {/* Name */}
          <KdsTextField
            label={t("addressDrawer.addressForm.inputs.name")}
            hasError={!!methods.formState.errors.name}
            {...methods.register("name")}
            dataCy="addressDrawerInputName"
            errorMessage={methods.formState.errors.name?.message}
          />
          {/* Recipient name */}
          <KdsTextField
            label={t("addressDrawer.addressForm.inputs.recipientName")}
            hasError={!!methods.formState.errors.recipientName}
            {...methods.register("recipientName")}
            dataCy="addressDrawerInputRecipientName"
            errorMessage={methods.formState.errors.recipientName?.message}
          />
          {/* Address: street & number */}
          <KdsTextField
            label={t("addressDrawer.addressForm.inputs.address")}
            hasError={!!methods.formState.errors.address}
            {...methods.register("address")}
            dataCy="addressDrawerInputAddress"
            errorMessage={methods.formState.errors.address?.message}
          />
          {/* Address: complement */}
          <KdsTextField
            label={t("addressDrawer.addressForm.inputs.complementaryInfo")}
            hasError={!!methods.formState.errors.complementaryInfo}
            {...methods.register("complementaryInfo")}
            dataCy="addressDrawerInputComplementaryInfo"
            errorMessage={methods.formState.errors.complementaryInfo?.message}
          />
          {/* Postal code */}
          <KdsTextField
            label={t("addressDrawer.addressForm.inputs.postalCode")}
            hasError={!!methods.formState.errors.postalCode}
            {...methods.register("postalCode")}
            dataCy="addressDrawerInputPostalCode"
            errorMessage={methods.formState.errors.postalCode?.message}
          />
          {/* City */}
          <KdsTextField
            label={t("addressDrawer.addressForm.inputs.city")}
            hasError={!!methods.formState.errors.city}
            {...methods.register("city")}
            dataCy="addressDrawerInputCity"
            errorMessage={methods.formState.errors.city?.message}
          />
          {/* Country */}
          <Controller
            control={methods.control}
            name="country"
            render={({ field, fieldState }) => (
              <CountryPicker
                label={t("addressDrawer.addressForm.inputs.country")}
                dataCy="addressDrawerInputCountry"
                hasError={
                  !!fieldState.error || !!methods.formState.errors.country
                }
                errorMessage={
                  methods.formState.errors.country?.message ??
                  t("inputs.requiredField")
                }
                onSelect={field.onChange}
              />
            )}
          />
          {/* Phone number */}
          <Controller
            name="phoneCountry"
            render={({
              field: { onChange: selectCountryChange, value: selectedCountry },
            }) => (
              <Controller
                control={methods.control}
                name="phoneNumber"
                render={({
                  field: { onChange: inputOnChange, value: inputValue },
                  fieldState: { error },
                }) => (
                  <Phone
                    label={t("addressDrawer.addressForm.inputs.phoneNumber")}
                    hasError={!!error}
                    errorMessage={error?.message}
                    inputOnChange={(newValue) =>
                      inputOnChange(trim(newValue.target.value))
                    }
                    inputValue={inputValue}
                    selectedCountry={selectedCountry}
                    onChangeSelectCountry={selectCountryChange}
                    dataCy="addressDrawerInputPhone"
                  />
                )}
              />
            )}
          />
        </div>
      </div>
    </>
  );
};
